import { useEffect, useState } from "react";

type Task = {
  id: string;
  brand_id: string;
  title: string;
  description?: string;
  status: string;
  kpi?: string | null;
  due_date?: string | null;
  created_at: string;
  created_by_role?: "admin" | "brand";
  created_by_name?: string | null;
  brand_name?: string | null;
  approved?: boolean | null;
  approval_note?: string | null;
  priority?: "low" | "normal" | "high" | "urgent" | string;
  requested_due_date?: string | null;
};

export default function IncomingTasks({
  api,
  safeDate,
  findKpiMeta,
}: {
  api: string;
  safeDate: (d: any) => string;
  findKpiMeta: (key: string) => { key: string; label: string; unit: "$" | "%" | "count" } | undefined;
}) {
  const [rows, setRows] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [noteById, setNoteById] = useState<Record<string, string>>({});
  const [statusById, setStatusById] = useState<Record<string, string>>({});

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`${api}/admin/tasks/incoming`, { credentials: "include" });
      const j = await r.json().catch(() => []);
      setRows(Array.isArray(j) ? j : []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function approve(id: string) {
    const body = {
      approval_note: noteById[id] || null,
      status: statusById[id] || "open",
    };
    const res = await fetch(`${api}/admin/tasks/${id}/approve`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (res.ok) setRows(prev => prev.filter(r => r.id !== id));
  }

  async function reject(id: string) {
    const body = { approval_note: noteById[id] || null };
    const res = await fetch(`${api}/admin/tasks/${id}/reject`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (res.ok) setRows(prev => prev.filter(r => r.id !== id));
  }

  if (loading) return <div className="text-center py-6 text-gray-500">Loading incoming…</div>;
  if (!rows.length) return <div className="text-center py-6 text-gray-500">No incoming brand requests 🎉</div>;

  return (
    <div className="space-y-2">
      {rows.map((t) => (
        <div key={t.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-all">
          <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-medium text-gray-900">{t.title}</div>
                {t.brand_name && <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs border border-gray-200">{t.brand_name}</span>}
                {t.created_by_name && <span className="px-2 py-0.5 rounded bg-gray-50 text-gray-700 text-xs border border-gray-200">by {t.created_by_name}</span>}
              </div>
              {t.description && <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{t.description}</p>}
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 mt-2">
                <span className="px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200">Requested</span>
                {t.kpi && <span className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200">{findKpiMeta(t.kpi)?.label || t.kpi}</span>}
                {t.priority && <span className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200 capitalize">{t.priority}</span>}
                {t.requested_due_date && <span className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200">Requested: {safeDate(t.requested_due_date)}</span>}
                {t.created_at && <span className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200">Created {safeDate(t.created_at)}</span>}
                <span className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200">ID: {t.id}</span>
              </div>
            </div>

            <div className="w-full sm:w-80 space-y-2">
              <select
                value={statusById[t.id] || "open"}
                onChange={(e) => setStatusById(s => ({ ...s, [t.id]: e.target.value }))}
                className="w-full text-sm h-10 px-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Set initial status upon approval"
              >
                <option value="open">Set status: Open</option>
                <option value="in_progress">Set status: In Progress</option>
                <option value="blocked">Set status: Blocked</option>
              </select>
              <textarea
                placeholder="Approval / rejection note (optional)"
                value={noteById[t.id] || ""}
                onChange={(e) => setNoteById(n => ({ ...n, [t.id]: e.target.value }))}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-pre-wrap break-words resize-y min-h-[88px]"
                rows={2}
              />
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:flex gap-2">
                <button onClick={() => approve(t.id)} className="w-full sm:w-auto h-10 px-3 rounded bg-green-600 text-white hover:bg-green-700 text-sm">Approve</button>
                <button onClick={() => reject(t.id)} className="w-full sm:w-auto h-10 px-3 rounded bg-red-600 text-white hover:bg-red-700 text-sm">Reject</button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}